import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { FormProvider, useForm } from "react-hook-form";
import { array, mixed, object, SchemaOf, string } from "yup";
import {
    ExamineeContactInfo,
    NewExamineeApi,
    NormalizedExaminee,
    parseExamineeForApi
} from "../../store/newExamineeSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    EthnicityOptions,
    ExistingExamineeDetails,
    RaceOptions,
    RoleId,
    Sex,
    Ethnicity,
    Race
} from "@store/generalTypes";
import { useModal } from "@services/ModalContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LCheckbox, LInputField, LRadio } from "@components";
import classNames from "classnames";
import { LSelect, SelectOption } from "@components/LSelect";
import { monthNames } from "@helpers/dateHelpers";
import { educationOptions, primaryLanguageOptions } from "../new-assessment-body/ExamineeDetails";
import { useAppSelector } from "@app/hooks";
import { useApi } from "@services/useApi";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { getDropdownLabel } from "@helpers/stringHelper";
import { isArray } from "lodash";

const formSchema: SchemaOf<NormalizedExaminee> = object({
    firstName: string().required(DefaultErrorMessages.required),
    lastName: string().required(DefaultErrorMessages.required),
    email: string().required(DefaultErrorMessages.required).email("Invalid email"),
    phoneNumber: string()
        .required(DefaultErrorMessages.required)
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number")),
    sex: mixed().oneOf(Object.values(Sex)).required(),
    birthDay: string()
        .required(DefaultErrorMessages.required)
        .test("dayRange", "Invalid Day", (val) => /\b(0?[1-9]|[12][0-9]|3[01])\b/.test(val!)),
    birthMonth: string()
        .required(DefaultErrorMessages.required)
        .test("monthRange", "Invalid Month", (val) => /^(1[0-2]|[1-9]|0[1-9])$/.test(val!)),
    birthYear: string()
        .required(DefaultErrorMessages.required)
        .min(4, "Enter a valid year")
        .test("yearRange", "Enter a valid year", (val) => {
            const valAsNumber = Number(val);
            return valAsNumber >= 1900 && valAsNumber < new Date().getFullYear();
        }),
    race: mixed().optional(),
    ethnicity: mixed().optional(),
    highestLevelEducation: string().optional(),
    primaryLanguage: string().required(DefaultErrorMessages.required),
    secondaryLanguages: array().of(string()).optional(),
    clinicianId: string().required(DefaultErrorMessages.required)
});

const months: SelectOption[] = monthNames.map((m, idx) => ({ name: m, value: `${idx + 1}` }));

export const EditExaminee = ({ contactInfo, details, handleUpdate }: EditExamineeProps): JSX.Element => {
    const validateMethods = useForm<NormalizedExaminee>({
        resolver: yupResolver(formSchema)
    });

    const { setFormName, onConfirm } = useModal();

    const labelClasses = classNames("label", "is-small", "mt-5");

    const errors = validateMethods.formState.errors;

    const [birthYear, birthMonth, birthDay] = details!.birthDate.split("-");

    const primaryLanguage = validateMethods.watch("primaryLanguage");
    const secondaryLanguages = validateMethods.watch("secondaryLanguages") as string[] | string | undefined;

    const [clinicians, setClinicians] = useState<SelectOption[]>([]);

    const location = useAppSelector((x) => x.orgLocations.currentLocation);
    const organization = useAppSelector((x) => x.organization);

    const onSubmit = async (data: NormalizedExaminee): Promise<void> => {
        const updatedExaminee = parseExamineeForApi(data);

        await handleUpdate(location!.id, details!.id, updatedExaminee);

        onConfirm?.();
    };
    const { getOrganizationUsers } = useApi();
    const getEthnicity = (ethnicity: string | number | undefined): string => {
        // Map string name to numeric value
        const ethnicityValue =
            typeof ethnicity === "string" ? Ethnicity[ethnicity as keyof typeof Ethnicity] : ethnicity;
        const ethnicityOption = EthnicityOptions.find((x) => x.value === String(ethnicityValue));
        return ethnicityOption?.name ?? "";
    };
    const getRace = (race: string | number | undefined): string => {
        // Map string name to numeric value
        const raceValue = typeof race === "string" ? Race[race as keyof typeof Race] : race;
        const raceOption = RaceOptions.find((x) => x.value === String(raceValue));
        return raceOption?.name ?? "";
    };

    const secondaryLanguageOptions = useMemo(
        () =>
            primaryLanguageOptions
                ?.filter(({ name }) => name !== primaryLanguage)
                .map(({ name }) => ({
                    optionName: "Secondary Language",
                    children: (
                        <LCheckbox
                            inputName="secondaryLanguages"
                            label={name}
                            value={name}
                            defaultChecked={details?.secondaryLanguages?.includes(name)}
                        />
                    )
                })) as DropdownMenuOption[],
        [primaryLanguage]
    );

    useEffect(() => {
        if (
            primaryLanguage !== undefined &&
            ((isArray(secondaryLanguages) && secondaryLanguages?.includes(primaryLanguage)) ||
                secondaryLanguages === primaryLanguage)
        ) {
            validateMethods.resetField("secondaryLanguages");
        }
    }, [primaryLanguage]);

    useEffect(() => {
        if (!!secondaryLanguages && !isArray(secondaryLanguages)) {
            validateMethods.setValue("secondaryLanguages", [secondaryLanguages]);
        }
    }, [secondaryLanguages]);

    const handleGetClinicians = useCallback(async () => {
        const clinicianUsers = (await getOrganizationUsers(organization.id!, [location!.id], [RoleId.Clinician])).items;

        const clinicianOptions = clinicianUsers.map((x) => {
            return { name: `${x.firstName} ${x.lastName}`, value: x.id };
        });

        setClinicians(clinicianOptions);
    }, []);

    useEffect(() => {
        setFormName?.("editExaminee");
        validateMethods.setValue("secondaryLanguages", []);
        void handleGetClinicians();
    }, []);

    return (
        <div className="container has-text-centered">
            <div className="mw-40 has-text-left">
                <FormProvider {...validateMethods}>
                    <form id="editExaminee" onSubmit={validateMethods.handleSubmit(onSubmit)}>
                        {/* CONTACT INFO */}
                        <div className="columns mb-0">
                            <div className="column">
                                <LInputField
                                    label="First Name"
                                    inputName="firstName"
                                    placeholder="Enter first name"
                                    defaultValue={contactInfo?.firstName}
                                />
                            </div>
                            <div className="column">
                                <LInputField
                                    label="Last Name"
                                    inputName="lastName"
                                    placeholder="Enter last name"
                                    defaultValue={contactInfo?.lastName}
                                />
                            </div>
                        </div>
                        <LInputField
                            label="Email Address"
                            inputName="email"
                            placeholder="Enter email address"
                            defaultValue={contactInfo?.email}
                        />
                        <LInputField
                            label="Phone Number"
                            inputName="phoneNumber"
                            placeholder="Enter phone number"
                            defaultValue={contactInfo?.phoneNumber}
                            maxLength={10}
                        />

                        {/* SEX */}
                        <label className={labelClasses}>1. Sex</label>
                        <div>
                            <LRadio
                                inputName="sex"
                                id={Sex.Male}
                                value={Sex.Male}
                                defaultChecked={details?.sex === Sex.Male}
                                shouldCap
                            />
                            <div className="is-flex is-flex-direction-row">
                                <LRadio
                                    inputName="sex"
                                    id={Sex.Female}
                                    value={Sex.Female}
                                    defaultChecked={details?.sex === Sex.Female}
                                    shouldCap
                                />
                                <LRadio
                                    inputName="sex"
                                    id={Sex.Other}
                                    value={Sex.Other}
                                    defaultChecked={details?.sex === Sex.Other}
                                    shouldCap
                                />
                            </div>
                            {errors.sex && <p className="help is-danger">{DefaultErrorMessages.required}</p>}
                        </div>

                        {/* BIRTHDATE */}
                        <label className={labelClasses}>2. Birthdate</label>
                        <div className="columns">
                            <div className="column is-5">
                                <LSelect
                                    options={months}
                                    label="Month"
                                    inputName="birthMonth"
                                    defaultOption={{ name: "Select Month", value: "" } as SelectOption}
                                    // Drops the 0 in '0x'
                                    defaultValue={Number(birthMonth).toString()}
                                />
                            </div>
                            <div className="column is-3">
                                <LInputField
                                    label="Day"
                                    inputName="birthDay"
                                    placeholder="DD"
                                    maxLength={2}
                                    isNumber
                                    defaultValue={birthDay}
                                />
                            </div>
                            <div className="column">
                                <LInputField
                                    label="Year"
                                    inputName="birthYear"
                                    placeholder="YYYY"
                                    maxLength={4}
                                    isNumber
                                    defaultValue={birthYear}
                                />
                            </div>
                        </div>

                        {/* RACE */}
                        <div className="mt-5">
                            <LSelect
                                options={RaceOptions}
                                label="3. Race (Optional)"
                                inputName="race"
                                defaultOption={{ name: "Select race", value: "0" } as SelectOption}
                                defaultValue={getRace(details?.race)}
                            />
                        </div>

                        {/* ETHNICITY */}
                        <div className="mt-5">
                            <LSelect
                                options={EthnicityOptions}
                                label="4. Ethnicity (Optional)"
                                inputName="ethnicity"
                                defaultOption={{ name: "Select ethnicity", value: "0" } as SelectOption}
                                defaultValue={getEthnicity(details?.ethnicity)}
                            />
                        </div>

                        {/* LEVEL OF EDUCATION */}
                        <div className="mt-5">
                            <LSelect
                                options={educationOptions}
                                label="5. Highest Level of Education (Optional)"
                                inputName="highestLevelEducation"
                                defaultOption={{ name: "Select level", value: "" } as SelectOption}
                                defaultValue={details?.highestLevelEducation}
                            />
                        </div>

                        {/* PRIMARY LANGUAGE */}
                        <div className="mt-5">
                            <LSelect
                                options={primaryLanguageOptions}
                                label="6. Primary Language"
                                inputName="primaryLanguage"
                                defaultOption={{ name: "Select language", value: "" } as SelectOption}
                                defaultValue={details?.primaryLanguage}
                            />
                        </div>

                        {/* SECONDARY LANGUAGE */}
                        {primaryLanguage && (
                            <div className="mt-5">
                                <LDropdownMenu
                                    id="secondaryLanguagesOptions"
                                    tag="7. Secondary Language (Optional)"
                                    label={getDropdownLabel(secondaryLanguages)}
                                    options={secondaryLanguageOptions}
                                    theme="light"
                                    variation="select"
                                    iconFamily="angle"
                                    iconColor={"#0028BD"}
                                    iconSize={"small"}
                                />
                            </div>
                        )}

                        <div className="divider" />
                        {clinicians.length ? (
                            <div className="mt-5">
                                <LSelect
                                    options={clinicians}
                                    label="Clinician"
                                    inputName="clinicianId"
                                    defaultOption={{ name: "Select clinician", value: "" } as SelectOption}
                                    defaultValue={details?.clinicianId}
                                    isFullWidth
                                />
                            </div>
                        ) : null}
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

interface EditExamineeProps {
    contactInfo?: ExamineeContactInfo;
    details?: ExistingExamineeDetails;
    handleUpdate: (locationId: number, id: number, details: NewExamineeApi) => Promise<void>;
}
